/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import {
  Avatar,
  FormControl,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  TextField,
} from '@material-ui/core';
import HelpOutline from '@material-ui/icons/HelpOutline';
import _ from 'lodash';

import * as actions from '../../../../state';
import styles from '../styles.module.css';
import DataSourceControlItems from './DataSourceControlItems';
import createChartNameFromSources from '../../../../utils/createChartNameFromSources';

const messages = defineMessages({
  CUSTOM_SOURCE_URL: {
    id: 'CUSTOM_SOURCE_URL',
    defaultMessage: 'Custom Data Source URL',
  },
  CUSTOM_CHART: {
    id: 'CUSTOM_CHART',
    defaultMessage: 'Custom Chart',
  },
  MATCHING_UNITS_HELPTEXT: {
    id: 'MATCHING_UNITS_HELPTEXT',
    defaultMessage: 'Adding more data is limited by the previously added data type.',
  },
  CUSTOM_SOURCE_URL_HELPTEXT: {
    id: 'CUSTOM_SOURCE_URL_HELPTEXT',
    defaultMessage: 'The URL of the web resource to be charted. The expected content is a JSON object like: {example}',
  },
  COLUMNS_AVAILABLE: {
    id: 'COLUMNS_AVAILABLE',
    defaultMessage: 'columns available',
  },
  MINUTES: {
    id: 'MINUTES',
    defaultMessage: 'Minutes',
  },
  SECONDS: {
    id: 'SECONDS',
    defaultMessage: 'Seconds',
  },
  AGENTS: {
    id: 'AGENTS',
    defaultMessage: 'Agents',
  },
  CALLS: {
    id: 'CALLS',
    defaultMessage: 'Calls',
  },
  PERCENT: {
    id: 'PERCENT',
    defaultMessage: 'Percent',
  },
  CUSTOM_FRAME_SOURCE: {
    id: 'CUSTOM_FRAME_SOURCE',
    defaultMessage: 'Custom Frame Source',
  },
  CUSTOM_FRAME_HELPTEXT: {
    id: 'CUSTOM_FRAME_HELPTEXT',
    defaultMessage: 'Provide content via a web resource.',
  },
  CUSTOM_DATA_SOURCE: {
    id: 'CUSTOM_DATA_SOURCE',
    defaultMessage: 'Custom Data Source',
  },
  CUSTOM_DATA_HELPTEXT: {
    id: 'CUSTOM_DATA_HELPTEXT',
    defaultMessage: 'Provide data via a web resource.',
  },
});

function DataSourceOptions({
  intl,
  selectedChartType,
  selectedSource,
  setTabContent,
  tabContent,
  updateSelectedSource,
}) {
  const dispatch = useDispatch();

  const board = useSelector(actions.readStateBoard);
  const chartsManifest = useSelector(actions.readChartsManifest);
  const selectedChart = useSelector(actions.readStateSelectedChart);
  const customDataEnabled = useSelector((state) => actions.selectConfig(state, 'ANALYTICS_ENABLE_CUSTOM_DATA_SOURCES'));

  const [statPopovers, setStatPopovers] = useState([]);

  const handleCustomUrl = (event) => {
    const customSourceIndex = selectedSource.index;

    const clonedChart = _.cloneDeep(selectedChart);

    clonedChart.data_sources.forEach((source) => {
      if (source.index === customSourceIndex) {
        source.queryParams.url = event.target.value; // eslint-disable-line no-param-reassign
        updateSelectedSource(source);
      }
    });

    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const selectCustomStat = () => {
    const clonedChart = _.cloneDeep(selectedChart);
    const customSourceIndex = clonedChart.data_sources.length;

    const newSource = {
      index: customSourceIndex, // put the index into the source so we can reference it directly later
      type: 'url',
      stat: 'custom',
      queryParams: { url: '' },
    };

    updateSelectedSource(newSource);
    setTabContent('configureSource');

    if (!clonedChart.name) {
      clonedChart.name = intl.formatMessage({ ...messages.CUSTOM_CHART });
    }

    clonedChart.data_sources.push(newSource);

    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const selectStat = (event, stat) => {
    const clonedChart = _.cloneDeep(selectedChart);
    const chartType = clonedChart.type;

    const chartDef = _.cloneDeep(chartsManifest[chartType]);
    const statDef = _.cloneDeep(chartDef.dataOptions[stat]);

    const newSource = {
      index: clonedChart.data_sources.length, // put the index into the source so we can reference it directly later
      type: statDef.type,
      stat: statDef.stat,
      formatType: statDef.formatType || statDef.defaultFormatType || null,
      socketEvents: statDef.socketEvents,
      socketSubscriptions: statDef.socketSubscriptions,
      queryParams: chartDef.queryParams, // default values for query parameters
    };

    if (clonedChart.data_sources.length) {
      newSource.queryParams.time_range = clonedChart.data_sources[0].queryParams.time_range;
    }

    /* if board has defaults filters */
    if (board.properties && board.properties.filters) {
      let filtersToApply = board.properties.filters;

      // trim out filters that don't meet the statDef's filterCategories
      if (statDef.filterCategories && statDef.filterCategories.length) {
        filtersToApply = filtersToApply.filter((filter) => statDef.filterCategories.includes(filter.type));
      }

      // if this statDef only supports single filters, trim the filter list to 1
      if (statDef.filterMultiples === false) {
        filtersToApply = filtersToApply.slice(0, 1);
      }

      newSource.filters = filtersToApply; // apply default filters
    }

    newSource.queryParams.domain = board.domain;

    if (newSource.socketSubscriptions) {
      newSource.socketSubscriptions = newSource.socketSubscriptions.map((event2) => ({
        ...event2, domain: board.domain,
      }));
    }

    if (statDef.categoryDefs && statDef.categoryDefs.length) {
      newSource.category = statDef.categoryDefs[0].category; // use first category as default
    }

    if (statDef.hasColumns) {
      newSource.queryParams.stats = statDef.defaultColumns; // toggle all columns by default
    }

    // non-column sources advance to stat select
    if (!statDef.hasColumns) {
      setTabContent('selectStat');
    } else {
      setTabContent('base');
    }

    clonedChart.data_sources.push(newSource);
    updateSelectedSource(newSource);
    setTabContent('configureSource');

    clonedChart.name = createChartNameFromSources(chartsManifest, clonedChart);
    clonedChart.renderRequired = true;

    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const defineStatPopover = (event, stat) => {
    event.preventDefault();

    const clonedStats = _.cloneDeep(statPopovers);

    clonedStats[stat] = event.currentTarget.tagName
      ? event.currentTarget
      : null;

    setStatPopovers(clonedStats);
  };

  const defineStatPopoverClose = (stat) => {
    const clonedStats = _.cloneDeep(statPopovers);
    clonedStats[stat] = null;
    setStatPopovers(clonedStats);
  };

  const renderCustomControls = () => {
    let exampleStr;

    switch (selectedChart.type) {
      case 'ResponsiveStat':
      case 'ResponsiveGauge':
        exampleStr = '{ "value": 3 }';
        break;
      case 'ResponsiveTable':
        // eslint-disable-next-line max-len
        exampleStr = '{ "headers": [ "headerOne", "headerTwo", "headerThree" ], "rows": [ [ "statusOne", 0.02, 64860 ], [ "statusTwo", 0.51, 124860 ] ] }';
        break;
      default:
        exampleStr = 'no example available for this type';
        break;
    }

    return (
      <ListItem>
        <FormControl style={{ width: '100%' }}>
          <TextField
            id="customSourceUrl"
            value={selectedChart.data_sources[0].queryParams.url || ''}
            label={<FormattedMessage {...messages.CUSTOM_SOURCE_URL} />}
            onChange={handleCustomUrl}
          />

          <FormHelperText>
            <FormattedMessage {...messages.CUSTOM_SOURCE_URL_HELPTEXT} values={{ example: exampleStr }} />
          </FormHelperText>
        </FormControl>
      </ListItem>
    );
  };

  const renderAvailableOptions = () => {
    const dataSourceOptions = [];
    const chartHasSources = selectedChart.data_sources.length;
    let baseStatDefUnit;

    if (chartHasSources) {
      const baseSourceStat = selectedChart.data_sources[0].stat;
      const baseStatDef = chartsManifest[selectedChartType].dataOptions[baseSourceStat];
      baseStatDefUnit = baseStatDef.unit;

      dataSourceOptions.push((
        <ListItem key="000_helptext" className={styles.helptext}>
          <FormattedMessage
            {...messages.MATCHING_UNITS_HELPTEXT}
            values={{ unit: baseStatDefUnit }}
          />
        </ListItem>
      ));
    }

    if (selectedChart.type === 'ResponsiveIFrame') {
      dataSourceOptions.push((
        <ListItem key="000_frame" className={styles.sourceOption} onClick={selectCustomStat}>
          <Avatar>C</Avatar>
          <ListItemText
            className={styles.sourceText}
            primary={<FormattedMessage {...messages.CUSTOM_FRAME_SOURCE} />}
            secondary={<FormattedMessage {...messages.CUSTOM_FRAME_HELPTEXT} />}
          />
        </ListItem>
      ));
    } else {
      Object.keys(chartsManifest[selectedChartType].dataOptions).forEach((stat, index) => { // iterate through data source manifest for stat definitions
        const statDef = chartsManifest[selectedChartType].dataOptions[stat];

        // if there are already sources attached to this chart, we need to restrict additional sources by unit
        if (chartHasSources && statDef.unit !== baseStatDefUnit) {
          return; // skip the rest of this loop if units did not match
        }

        if (statDef.supportedCharts.includes(selectedChartType)) { // only show options for the selected chart
          let secondaryLabel;

          if (statDef.hasCategories) {
            secondaryLabel = statDef.categoryDefs.map((categoryDef) => categoryDef.label).join(', ');
          }

          if (statDef.hasColumns) {
            // eslint-disable-next-line max-len
            secondaryLabel = `${statDef.supportedColumns.length} ${intl.formatMessage({ ...messages.COLUMNS_AVAILABLE })}`;
          } else if (messages[statDef.unit.toUpperCase()]) {
            secondaryLabel = intl.formatMessage({ ...messages[statDef.unit.toUpperCase()] });
          }

          dataSourceOptions.push((
            <ListItem key={index} className={styles.sourceOption} onClick={(e) => selectStat(e, stat)}>
              <Avatar className={styles.sourceOptionAvatar}>
                {stat.length < 4 ? stat : ' '}
              </Avatar>

              <ListItemText
                className={styles.sourceText}
                primary={statDef.label}
                secondary={secondaryLabel}
              />

              {statDef.description // if a description is available, add a popover element
                && (
                  <div style={{ float: 'right' }}>
                    <IconButton
                      style={{ padding: 0 }}
                      onMouseEnter={(e) => defineStatPopover(e, stat)}
                      onMouseLeave={() => defineStatPopoverClose(stat)}
                    >
                      <HelpOutline />
                    </IconButton>
                    <Popover
                      open={Boolean(statPopovers[stat])}
                      anchorEl={statPopovers[stat]}
                      style={{ pointerEvents: 'none' }}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <div style={{ maxWidth: 300 }} className={styles.defineStatPopover}>{statDef.description}</div>
                    </Popover>
                  </div>
                )}
            </ListItem>
          ));
        }
      });

      /* the uiconfig ANALYTICS_ENABLE_CUSTOM_DATA_SOURCES can be set to enable
           * the custom data source option */
      if (customDataEnabled && ['ResponsiveGauge', 'ResponsiveStat', 'ResponsiveTable'].includes(selectedChart.type)) {
        dataSourceOptions.push((
          <ListItem key="000_cus" className={styles.sourceOption} onClick={selectCustomStat}>
            <Avatar className={styles.sourceOptionAvatar}>
              C
            </Avatar>
            <ListItemText
              className={styles.sourceText}
              primary={<FormattedMessage {...messages.CUSTOM_DATA_SOURCE} />}
              secondary={<FormattedMessage {...messages.CUSTOM_DATA_HELPTEXT} />}
            />
          </ListItem>
        ));
      }
    }

    const tabClass = ['selectStat'].includes(tabContent)
      ? styles.controlsContainerNoTab
      : styles.controlsContainer;

    return (
      <List className={tabClass}>
        {dataSourceOptions}
      </List>
    );
  };

  const renderSourceControls = () => {
    const { stat } = selectedSource;
    const chartDef = chartsManifest[selectedChartType];
    const statDef = chartDef.dataOptions[stat];

    return (
      <DataSourceControlItems
        statDef={statDef}
        tabContent={tabContent}
        chartOptions={chartDef.chartOptions}
        selectedSource={selectedSource}
        updateSelectedSource={updateSelectedSource}
      />
    );
  };

  const chartHasSources = selectedChart.data_sources.length;
  const chartHasCustomSource = chartHasSources && selectedChart.data_sources[0].stat === 'custom';

  if (chartHasCustomSource) {
    return [
      renderCustomControls(),
      renderSourceControls(),
    ];
  }

  if (tabContent === 'configureSource' || tabContent === 'toggleColumns') {
    return renderSourceControls();
  }

  return renderAvailableOptions();
}

DataSourceOptions.defaultProps = {
  selectedSource: null,
};

DataSourceOptions.propTypes = {
  intl: PropTypes.shape({}).isRequired,
  selectedChartType: PropTypes.string.isRequired,
  selectedSource: PropTypes.shape({}),
  setTabContent: PropTypes.func.isRequired,
  tabContent: PropTypes.string.isRequired,
  updateSelectedSource: PropTypes.func.isRequired,
};

export default injectIntl(DataSourceOptions);
